'use client';

import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { Avatar } from '@/components/ds';
import { cn } from '@/lib/utils';
import type { WorkshopPoster } from '@/lib/workshops/types';

// Tappable poster identity → /u/[handle]. The whole row is one Link, so the
// ds Avatar is rendered WITHOUT username (no nested anchors).
export default function PosterIdentity({
  poster,
  size = 'row',
  showTagline = false,
}: {
  poster: WorkshopPoster | null;
  size?: 'row' | 'lg';
  showTagline?: boolean;
}) {
  const lg = size === 'lg';
  const name = poster?.name || 'Unknown poster';
  const handle = poster?.handle || null;
  const secondary = showTagline ? poster?.tagline || (handle ? `@${handle}` : '') : handle ? `@${handle}` : '';

  const inner = (
    <>
      <Avatar src={poster?.avatar || undefined} name={name} size={lg ? 44 : 30} online />
      <span className="min-w-0 flex flex-col" style={{ gap: lg ? 2 : 0 }}>
        <span className="flex items-center gap-1.5 min-w-0">
          <span
            className={cn(
              'font-semibold text-text-primary truncate',
              lg ? 'text-[15px]' : 'text-[13px]',
            )}
          >
            {name}
          </span>
          {handle && (
            <ChevronRight
              className={cn('text-text-tertiary flex-shrink-0', lg ? 'w-3.5 h-3.5' : 'w-3 h-3')}
              strokeWidth={1.75}
            />
          )}
        </span>
        {secondary && (
          <span
            className={cn('text-text-tertiary truncate', lg ? 'text-[12.5px]' : 'text-[11.5px]')}
          >
            {secondary}
          </span>
        )}
      </span>
    </>
  );

  if (!handle) {
    return <span className="flex items-center gap-2.5 min-w-0">{inner}</span>;
  }
  return (
    <Link
      href={`/u/${encodeURIComponent(handle)}`}
      className="group/wkname flex items-center gap-2.5 min-w-0 hover:[&_.font-semibold]:underline"
      style={{ gap: lg ? 11 : 9 }}
    >
      {inner}
    </Link>
  );
}
